import Cookies from "js-cookie";

export const YANDEX_METRIKA_COUNTER_ID = 98998986;

const YANDEX_METRIKA_SCRIPT_ID = "yandex-metrika-script";
const YANDEX_METRIKA_SCRIPT_SRC = "https://mc.yandex.ru/metrika/tag.js";
const YANDEX_METRIKA_DISABLE_FLAG = `disableYaCounter${YANDEX_METRIKA_COUNTER_ID}`;

const YANDEX_METRIKA_COOKIE_NAMES = Object.freeze([
  "_ym_metrika_enabled",
  "_ym_isad",
  "_ym_uid",
  "_ym_fa",
  "_ym_d",
  "_ym_ucs",
  "_ym_debug",
  "_ym_hostIndex",
  "_ym_sup_debug",
]);

const YANDEX_METRIKA_COOKIE_PREFIXES = Object.freeze([
  "_ym_visorc_",
]);

const YANDEX_METRIKA_LOCAL_STORAGE_NAMES = Object.freeze([
  `_ym${YANDEX_METRIKA_COUNTER_ID}_lastHit`,
  `_ym${YANDEX_METRIKA_COUNTER_ID}_lsid`,
  `_ym${YANDEX_METRIKA_COUNTER_ID}_reqNum`,
  "_ym_hide_phones",
  "_ym_retryReqs",
  "_ym_uid",
  "zz",
]);

const YANDEX_METRIKA_SESSION_STORAGE_NAMES = Object.freeze([
  "_ym_debugger_state",
  "_ym_turbo_uid",
]);

let scriptLoadPromise = null;
let shouldBeEnabled = false;
let isInitialized = false;

function canUseBrowserApi() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function setCollectionDisabled(isDisabled) {
  if (!canUseBrowserApi()) {
    return;
  }

  window[YANDEX_METRIKA_DISABLE_FLAG] = isDisabled;
}

function ensureMetrikaQueue() {
  if (typeof window.ym === "function") {
    return window.ym;
  }

  const ym = function () {
    ym.a = ym.a || [];
    ym.a.push(arguments);
  };

  ym.l = Date.now();
  window.ym = ym;

  return ym;
}

function findMetrikaScript() {
  const scriptById = document.getElementById(YANDEX_METRIKA_SCRIPT_ID);

  if (scriptById) {
    return scriptById;
  }

  for (let index = 0; index < document.scripts.length; index += 1) {
    if (document.scripts[index].src === YANDEX_METRIKA_SCRIPT_SRC) {
      return document.scripts[index];
    }
  }

  return null;
}

function loadMetrikaScript() {
  if (!canUseBrowserApi()) {
    return Promise.resolve(false);
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  ensureMetrikaQueue();

  let script = findMetrikaScript();

  if (script && script.getAttribute("data-loaded") === "true") {
    return Promise.resolve(true);
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const handleLoad = () => {
      script.setAttribute("data-loaded", "true");
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
      resolve(true);
    };

    const handleError = () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
      reject(new Error("Failed to load Yandex Metrica"));
    };

    if (!script) {
      script = document.createElement("script");
      script.id = YANDEX_METRIKA_SCRIPT_ID;
      script.src = YANDEX_METRIKA_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);
  }).catch(() => {
    scriptLoadPromise = null;

    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }

    return false;
  });

  return scriptLoadPromise;
}

function getCookieDomainCandidates() {
  if (!canUseBrowserApi()) {
    return [];
  }

  const hostname = window.location.hostname;

  if (
    !hostname ||
    hostname === "localhost" ||
    hostname.indexOf(".") === -1 ||
    hostname.indexOf(":") !== -1 ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  ) {
    return [];
  }

  const hostnameParts = hostname.split(".");
  const rootDomain = hostnameParts.slice(-2).join(".");

  return [
    rootDomain,
    `.${rootDomain}`,
  ];
}

function isMetrikaCookie(cookieName) {
  if (YANDEX_METRIKA_COOKIE_NAMES.includes(cookieName)) {
    return true;
  }

  return YANDEX_METRIKA_COOKIE_PREFIXES.some((prefix) => (
    cookieName.startsWith(prefix)
  ));
}

function removeMetrikaCookie(cookieName) {
  Cookies.remove(cookieName, {
    path: "/",
  });

  getCookieDomainCandidates().forEach((domain) => {
    Cookies.remove(cookieName, {
      path: "/",
      domain,
    });
  });
}

function clearMetrikaCookies() {
  Object.keys(Cookies.get())
    .filter(isMetrikaCookie)
    .forEach(removeMetrikaCookie);
}

function clearStorageItems(storageName, itemNames) {
  if (!canUseBrowserApi()) {
    return;
  }

  try {
    const storage = window[storageName];

    itemNames.forEach((itemName) => {
      storage.removeItem(itemName);
    });
  } catch {
    return;
  }
}

function clearStoredData() {
  clearMetrikaCookies();

  clearStorageItems(
    "localStorage",
    YANDEX_METRIKA_LOCAL_STORAGE_NAMES
  );

  clearStorageItems(
    "sessionStorage",
    YANDEX_METRIKA_SESSION_STORAGE_NAMES
  );
}

function enable() {
  if (!canUseBrowserApi()) {
    return Promise.resolve(false);
  }

  shouldBeEnabled = true;
  setCollectionDisabled(false);

  return loadMetrikaScript().then((isLoaded) => {
    if (!isLoaded || !shouldBeEnabled) {
      return false;
    }

    setCollectionDisabled(false);

    if (!isInitialized) {
      window.ym(YANDEX_METRIKA_COUNTER_ID, "init", {
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
      });

      isInitialized = true;
    }

    return true;
  });
}

function disable() {
  shouldBeEnabled = false;

  if (!canUseBrowserApi()) {
    return;
  }

  setCollectionDisabled(true);

  if (isInitialized && typeof window.ym === "function") {
    window.ym(YANDEX_METRIKA_COUNTER_ID, "destruct");
  }

  isInitialized = false;
  clearStoredData();
}

export const yandexMetrikaService = Object.freeze({
  enable,
  disable,
  clearStoredData,
});
